import { Controller } from "stimulus"

const SWITCHER_STYLE = {
  with_name: true,
  full_name: true,
  is_dropdown: true,
  with_flags: true,
  flag_type: "circle"
}

const DYNAMIC_LABEL_SELECTOR = '[data-wg-dynamic-key]'

export default class extends Controller {
  static values = {
    apiKey: String
  }

  connect() {
    this.defaultLang = this.normalizeLang(document.documentElement.getAttribute('lang') || 'fr')

    this.refreshHandler = this.refresh.bind(this)
    this.beforeCacheHandler = this.handleBeforeCache.bind(this)

    document.addEventListener('turbolinks:load', this.refreshHandler)
    document.addEventListener('turbolinks:render', this.refreshHandler)
    document.addEventListener('turbolinks:before-cache', this.beforeCacheHandler)

    this.refresh()
  }

  disconnect() {
    document.removeEventListener('turbolinks:load', this.refreshHandler)
    document.removeEventListener('turbolinks:render', this.refreshHandler)
    document.removeEventListener('turbolinks:before-cache', this.beforeCacheHandler)

    if (this.waitForWeglotTimeout) {
      clearTimeout(this.waitForWeglotTimeout)
      this.waitForWeglotTimeout = undefined
    }

    this.detachLanguageHandler()
  }

  refresh() {
    if (!this.shouldEnable()) {
      this.hideSwitcher()
      this.updateDynamicLabels(this.defaultLang)
      return
    }

    if (!window.Weglot) {
      this.scheduleRetry()
      return
    }

    this.showSwitcher()

    if (!window._tomoWeglotInitialized) {
      this.initializeWeglot()
    } else {
      if (!this.hasRenderedContent()) {
        this.resetSwitcher()
        this.initializeWeglot()
        return
      }

      this.renderSwitcher()
      this.defer(() => {
        const lang = this.syncLanguage()
        this.refreshDom()
        this.updateDynamicLabels(lang)
      })
    }
  }

  handleBeforeCache() {
    this.hideSwitcher()
  }

  shouldEnable() {
    const body = document.body
    if (!body) return false
    return body.getAttribute('data-weglot-enabled') === 'true'
  }

  showSwitcher() {
    this.element.style.display = ''
  }

  hideSwitcher() {
    this.element.style.display = 'none'
  }

  scheduleRetry() {
    this.hideSwitcher()
    if (this.waitForWeglotTimeout) return

    this.waitForWeglotTimeout = setTimeout(() => {
      this.waitForWeglotTimeout = undefined
      this.refresh()
    }, 100)
  }

  initializeWeglot() {
    const apiKey = this.apiKeyValue
    if (!apiKey) return

    this.element.removeAttribute('aria-hidden')
    this.element.innerHTML = ''

    window.Weglot.initialize({
      api_key: apiKey,
      switchers: [{
        element: `#${this.element.id}`,
        style: SWITCHER_STYLE
      }]
    })
    window._tomoWeglotInitialized = true

    if (!this.languageChangeHandler && typeof window.Weglot.on === 'function') {
      this.languageChangeHandler = (lang) => {
        document.documentElement.setAttribute('lang', lang)
        this.updateDynamicLabels(this.normalizeLang(lang))
      }
      window.Weglot.on('languageChanged', this.languageChangeHandler)
    }

    this.defer(() => {
      const lang = this.syncLanguage()
      this.refreshDom()
      this.updateDynamicLabels(lang)
    })
  }

  renderSwitcher() {
    if (typeof window.Weglot.renderSwitchers === 'function') {
      try { window.Weglot.renderSwitchers() } catch (error) { /* noop */ }
    }
  }

  hasRenderedContent() {
    return this.element && this.element.childElementCount > 0
  }

  resetSwitcher() {
    this.detachLanguageHandler()
    this.element.innerHTML = ''
    window._tomoWeglotInitialized = false
  }

  syncLanguage() {
    if (!window.Weglot) return
    try {
      if (typeof window.Weglot.getCurrentLang === 'function') {
        const lang = window.Weglot.getCurrentLang()
        if (lang) {
          if (typeof window.Weglot.switchTo === 'function') {
            window.Weglot.switchTo(lang)
          }
          document.documentElement.setAttribute('lang', lang)
          return this.normalizeLang(lang)
        }
      }
    } catch (error) {
      // noop
    }
    return this.defaultLang
  }

  refreshDom() {
    if (window.Weglot && typeof window.Weglot.refreshAllDom === 'function') {
      try { window.Weglot.refreshAllDom() } catch (error) { /* noop */ }
    }
  }

  defer(callback) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback)
    } else {
      setTimeout(callback, 0)
    }
  }

  detachLanguageHandler() {
    if (this.languageChangeHandler && window.Weglot && typeof window.Weglot.off === 'function') {
      try { window.Weglot.off('languageChanged', this.languageChangeHandler) } catch (error) { /* noop */ }
    }
    this.languageChangeHandler = null
  }

  updateDynamicLabels(lang) {
    const normalizedLang = this.normalizeLang(lang) || this.defaultLang
    const fallback = this.defaultLang
    const targetKey = this.datasetKeyForLang(normalizedLang)
    const fallbackKey = this.datasetKeyForLang(fallback)

    document.querySelectorAll(DYNAMIC_LABEL_SELECTOR).forEach((element) => {
      const text = element.dataset[targetKey] || element.dataset[fallbackKey]
      if (text) {
        element.textContent = text
      }
    })
  }

  datasetKeyForLang(lang) {
    if (!lang) return undefined
    const normalized = this.normalizeLang(lang)
    if (!normalized) return undefined
    return `wgLang${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
  }

  normalizeLang(lang) {
    if (!lang) return undefined
    return String(lang).toLowerCase().split('-')[0]
  }
}
